import React, { FC, useEffect, useState, Fragment } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import convertNumbThousand from "utils/convertNumbThousand";
import { debounce } from "lodash";
import {
  getPropertyCategories,
  getPropertyType,
  getAmenitiesProperty,
  getFeaturesProperty,
  searchCategory,
  getRoomType,
} from "../../redux/reducers/Properties/PropertiesSlice";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import moment from "moment";
import { Dialog, Popover, Transition } from "@headlessui/react";
import Checkbox from "shared/Checkbox/Checkbox";
import ButtonThird from "shared/Button/ButtonThird";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SearchInput from "components/NcInputNumber/SearchInput";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { setSearchData } from "redux/reducers/Search/SearchSlice";
import { RoomType } from "redux/reducers/Properties/PropertiesSliceTypes";
import { getIconComponent } from "shared/Icon/IconDymamiclisting";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
// At the top of TabFilters.tsx, add:
import PropertyTypeFilter from "./components/PropertyTypeFilter";
import PriceRangeFilter from "./components/PriceRangeFilter";
import RoomsAndBedsFilter from "./components/RoomsAndBedsFilter";
import DatesFilter from "./components/DatesFilter";
import MobileFiltersModal from "./components/MobileFiltersModal";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

export interface TabFiltersProps {
  page?: number;
}

const TabFilters: FC<TabFiltersProps> = ({ page }) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    propertiesCategories,
    propertyTypes,
    AmenitiesCategories,
    featuresCategories,
  } = useSelector((state: RootState) => state.property);

  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );
  const {
    latitude,
    longitude,
    label,
    startDate,
    endDate,
    roomTypeId,
    selectedAmenitiesId,
    propertyTypeId,
    priceMax,
    priceMin,
    bedroom,
    bathroom,
    propertyCategories,
    feature,
  } = storedSearchData || {};

  const [filteredPropertyTypes, setFilteredPropertyTypes] = useState<any[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>(
    priceMin != null && priceMax != null ? [priceMin, priceMax] : [0, 5000]
  );

  const [isOpenMoreFilter, setisOpenMoreFilter] = useState(false);
  const closeModalMoreFilter = () => setisOpenMoreFilter(false);
  const openModalMoreFilter = () => setisOpenMoreFilter(true);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [datesRange, setDatesRange] = useState<[Date | null, Date | null]>([
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ]);

  useEffect(() => {
    dispatch(getPropertyCategories());
    dispatch(getPropertyType());
    dispatch(getAmenitiesProperty());
    dispatch(getFeaturesProperty());
  }, [dispatch]);

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await dispatch(getRoomType()).unwrap();
        if (response.success) {
          setRoomTypes(response.data);
        } else {
          console.error("Failed to fetch room types:", response.message);
        }
      } catch (error) {
        console.error("Error fetching room types:", error);
      }
    };

    fetchRoomTypes();
  }, [dispatch]);

  useEffect(() => {
  console.log("📊 TabFilters MOUNTED - Redux propertyTypeId:", propertyTypeId);
  console.log("📊 TabFilters MOUNTED - Split into array:", propertyTypeId?.split(",").filter(Boolean));
  console.log("📊 TabFilters MOUNTED - Formik initial property_type_ids:", formik.values.property_type_ids);
}, []);



  useEffect(() => {
    if (Array.isArray(propertyTypes)) {
      setFilteredPropertyTypes(propertyTypes);
    }
  }, [propertyTypes]);

  

  const handlePropertyTypeChange = (ids: string[]) => {
    formik.setFieldValue("property_type_ids", ids);
    dispatch(
      setSearchData({
        ...storedSearchData,
        propertyTypeId: ids.join(","), // Store as comma-separated string in Redux
      })
    );
  };

  const propertyTypeIdsArray = propertyTypeId
    ? propertyTypeId.split(",").filter(Boolean)
    : [];

  const handleBedroomsChange = (value: number) => {
    formik.setFieldValue("bedrooms", value);
    dispatch(
      setSearchData({
        ...storedSearchData,
        bedroom: value,
      })
    );
  };

  const handleBathroomsChange = (value: number) => {
    formik.setFieldValue("bathrooms", value);
    dispatch(
      setSearchData({
        ...storedSearchData,
        bathroom: value,
      })
    );
  };

  const handleAmenitiesChange = (selectedAmenities: string[]) => {
    formik.setFieldValue("amenities", selectedAmenities);
    dispatch(
      setSearchData({
        ...storedSearchData,
        selectedAmenitiesId: selectedAmenities,
      })
    );
  };

  const handleFeaturesChange = (selectedFeatures: string[]) => {
    formik.setFieldValue("features", selectedFeatures);
    dispatch(
      setSearchData({
        ...storedSearchData,
        feature: selectedFeatures,
      })
    );
  };

  const handlePropertyCategoryChange = (selectedCategories: string[]) => {
    formik.setFieldValue("property_category", selectedCategories);
    dispatch(
      setSearchData({
        ...storedSearchData,
        propertyCategories: selectedCategories,
      })
    );
  };

  const handleRoomTypeChange = (selectedRoomType: string) => {
    formik.setFieldValue("room_type_id", selectedRoomType);
    dispatch(
      setSearchData({
        ...storedSearchData,
        roomTypeId: selectedRoomType,
      })
    );
  };

  const addMonths = (date: Date, months: number) => {
    const result = new Date(date);
    const d = result.getDate();
    result.setMonth(result.getMonth() + months);

    if (result.getDate() < d) {
      result.setDate(0);
    }
    return result;
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [newStart, newEnd] = dates;
    setDatesRange([newStart, newEnd]);
    formik.setFieldValue("startDate", newStart);
    formik.setFieldValue("endDate", newEnd);

    dispatch(
      setSearchData({
        ...storedSearchData,
        startDate: newStart?.toISOString() ?? null,
        endDate: newEnd?.toISOString() ?? null,
      })
    );
  };

  const handleMinStayChange = (months: number) => {
    const [start, end] = datesRange;
    const currentValue = formik.values.minStayMonths;
    const newValue = currentValue === months ? null : months;
    formik.setFieldValue("minStayMonths", newValue);

    if (newValue && start) {
      const newEnd = addMonths(start, newValue);
      setDatesRange([start, newEnd]);
      formik.setFieldValue("endDate", newEnd);

      dispatch(
        setSearchData({
          ...storedSearchData,
          startDate: start?.toISOString() ?? null,
          endDate: newEnd.toISOString(),
        })
      );
    } else if (!newValue) {
      setDatesRange([start, end]);
      formik.setFieldValue("endDate", end);
    }
  };

  const clearDates = () => {
    setDatesRange([null, null]);
    formik.setFieldValue("startDate", null);
    formik.setFieldValue("endDate", null);
    formik.setFieldValue("minStayMonths", null);

    dispatch(
      setSearchData({
        ...storedSearchData,
        startDate: null,
        endDate: null,
        minStayMonths: null,
      })
    );
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      location: label || "",
      startDate: startDate || "",
      endDate: endDate || "",
      property_category: Array.isArray(propertyCategories)
        ? propertyCategories
        : [],
      property_type_ids: propertyTypeId
        ? propertyTypeId.split(",").filter(Boolean)
        : [], // ✅ Changed to array
      amenities: Array.isArray(selectedAmenitiesId) ? selectedAmenitiesId : [],
      features: Array.isArray(feature) ? feature : [],
      price_min: priceMin || priceRange[0],
      price_max: priceMax || priceRange[1],
      guest_number: "",
      bedrooms: bedroom || "",
      bathrooms: bathroom || "",
      room_type_id: roomTypeId || "",
      minStayMonths: null,
    },
    onSubmit: () => {},
  });

  useEffect(() => {
  console.log("🔄 TabFilters - Formik values updated:", formik.values.property_type_ids);
}, [formik.values.property_type_ids]);

  const triggerSearch = debounce(
    async (
      minPrice: number,
      maxPrice: number,
      values: any,
      isMultipleFilter?: boolean,
      isBudgetFilter?: boolean,
      isRoomFilter?: boolean
    ) => {
      const params = new URLSearchParams();

      // In triggerSearch function:
      if (
        Array.isArray(values.property_type_ids) &&
        values.property_type_ids.length > 0
      ) {
        values.property_type_ids.forEach((id: string) => {
          params.append("q[property_type_ids][]", id);
        });
      }

      if (Array.isArray(values.property_category)) {
        values.property_category.forEach((id: any) => {
          params.append("q[property_category_ids][]", id);
        });
      }

      if (values.room_type_id) {
        params.append("q[room_type_ids]", values.room_type_id);
      }

      if (typeof minPrice === "number")
        params.append("q[min_price]", minPrice.toString());

      if (typeof maxPrice === "number")
        params.append("q[max_price]", maxPrice.toString());

      if (values.bedrooms)
        params.append("q[total_bedrooms_gteq]", values.bedrooms);

      if (values.guest_number)
        params.append("q[guest_number_gteq]", values.guest_number);

      if (values.bathrooms)
        params.append("q[total_bathrooms_gteq]", values.bathrooms);

      if (latitude) params.append("q[latitude]", latitude.toString());
      if (longitude) params.append("q[longitude]", longitude.toString());

       // ✅ ADD NEIGHBORHOOD FILTER
        if (storedSearchData?.locationData?.neighborhood) {
          params.append("q[neighborhood]", storedSearchData.locationData.neighborhood);
          console.log("✅ Including neighborhood filter:", storedSearchData.locationData.neighborhood);
        }

        // ✅ ADD CITY FILTER
        if (storedSearchData?.locationData?.city) {
          params.append("q[city]", storedSearchData.locationData.city);
          console.log("✅ Including city filter:", storedSearchData.locationData.city);
        }

        // ✅ ADD RADIUS (use dynamic radius based on location specificity)
        const radius = storedSearchData?.locationData?.neighborhood ? 3 : 
                      storedSearchData?.locationData?.city ? 5 : 10;
        params.append("q[radius]", radius.toString());
        console.log("✅ Search radius:", radius, "km");

      if (startDate) {
        const startFormatted = moment(startDate).format("YYYY-MM-DD");
        params.append("q[availability_start_lteq]", startFormatted);
      }

      if (endDate) {
        const endFormatted = moment(endDate).format("YYYY-MM-DD");
        params.append("q[availability_end_gteq]", endFormatted);
      }

      if (page) params.append("page", page.toString());

      if (Array.isArray(values.amenities)) {
        values.amenities.forEach((id: any) => {
          params.append("q[amenity_ids][]", id);
        });
      }

      if (Array.isArray(values.features)) {
        values.features.forEach((id: any) => {
          params.append("q[feature_ids][]", id);
        });
      }

      const searchQuery = params.toString();

      try {
        const action = await dispatch(searchCategory(searchQuery));

        if (action.payload?.success) {
          dispatch(
            setSearchData({
              ...storedSearchData,
              searchedResult: action.payload,
              propertyCategories: isMultipleFilter
                ? []
                : storedSearchData.propertyCategories || [],
              selectedAmenitiesId: isMultipleFilter
                ? []
                : storedSearchData.selectedAmenitiesId || [],
              feature: isMultipleFilter ? [] : storedSearchData.feature || [],
              roomTypeId: isMultipleFilter
                ? ""
                : storedSearchData.roomTypeId || "",
              priceMin: isBudgetFilter ? 0 : storedSearchData.priceMin || 0,
              priceMax: isBudgetFilter
                ? 5000
                : storedSearchData.priceMax || 5000,
              bedroom: isRoomFilter ? "" : storedSearchData.bedroom || "",
              bathroom: isRoomFilter ? "" : storedSearchData.bathroom || "",
            })
          );
        } else {
          dispatch(setSearchData({ ...storedSearchData, searchedResult: [] }));
        }
      } catch (error) {
        console.error("[DEBUG] Search API error:", error);
      }
    },
    300
  );

  const handleApplyFilters = async (close: () => void) => {
    await triggerSearch(priceRange[0], priceRange[1], formik.values);
    close();
  };

  const handlePriceChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      setPriceRange(value as [number, number]);
      formik.setFieldValue("price_min", value[0]);
      formik.setFieldValue("price_max", value[1]);

      dispatch(
        setSearchData({
          ...storedSearchData,
          priceMin: value[0],
          priceMax: value[1],
        })
      );
    } else {
      setPriceRange([value, value]);
      formik.setFieldValue("price_min", value);
      formik.setFieldValue("price_max", value);

      dispatch(
        setSearchData({
          ...storedSearchData,
          priceMin: value,
          priceMax: value,
        })
      );
    }
  };

  const handleClear = async (close: () => void) => {
    setPriceRange([0, 5000]);
    await formik.setFieldValue("price_min", 0);
    await formik.setFieldValue("price_max", 5000);

    const updatedValues = {
      ...formik.values,
      price_min: 0,
      price_max: 5000,
    };

    dispatch(
      setSearchData({
        ...storedSearchData,
        priceMin: 0,
        priceMax: 5000,
      })
    );

    await triggerSearch(0, 5000, updatedValues, true, true);

    close();
  };

  const handleClearParticularFilters = () => {
    const updatedValues = {
      ...formik.values,
      amenities: [],
      features: [],
      property_category: [],
      room_type_id: "",
    };

    const resetSearchData = {
      ...storedSearchData,
      selectedAmenitiesId: [],
      feature: [],
      propertyCategories: [],
      roomTypeId: "",
    };
    dispatch(setSearchData(resetSearchData));

    formik.setValues(updatedValues);
    closeModalMoreFilter();

    setTimeout(() => {
      triggerSearch(
        updatedValues.price_min,
        updatedValues.price_max,
        updatedValues,
        true
      );
    }, 0);
  };

  const handleClearRoomAndBeds = (close: () => void) => {
    const updatedValues = {
      ...formik.values,
      bedrooms: "",
      bathrooms: "",
    };

    formik.setValues(updatedValues);
    close();

    setTimeout(() => {
      triggerSearch(
        updatedValues.price_min || 0,
        updatedValues.price_max || 0,
        updatedValues,
        true,
        false,
        true
      );
    }, 0);
  };

  const renderXClear = () => {
    return (
      <span className="w-4 h-4 rounded-full bg-white/30 dark:bg-white/20 text-white flex items-center justify-center ml-3 cursor-pointer hover:bg-white/40 dark:hover:bg-white/30 transition-colors">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    );
  };

  /*  const renderTabsTypeOfPlace = () => {
    const selectedType =
      filteredPropertyTypes.find(
        (item) => String(item.id) === formik.values.property_type_id
      )?.name || "Type of property";

    const isFilterApplied = !!formik.values.property_type_id;

    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border focus:outline-none transition-all ${
                open ? "!border-primary-500" : ""
              } ${
                isFilterApplied
                  ? "bg-[#f5694b] text-white border-[#f5694b]"
                  : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
              }`}
            >
              <span>
                {formik.values.property_type_id
                  ? selectedType
                  : "Type of property"}
              </span>
              <i className="las la-angle-down ml-2"></i>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-3 left-0 sm:px-0 lg:max-w-md">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  <div className="relative flex flex-col px-5 py-6 space-y-5">
                    <div>
                      <Checkbox
                        name="property_type_id"
                        label="All Types"
                        isChecked={!formik.values.property_type_id}
                        onChange={() =>
                          formik.setFieldValue("property_type_id", "")
                        }
                      />
                    </div>
                    {filteredPropertyTypes.map((item: any) => (
                      <div key={item.id}>
                        <Checkbox
                          name="property_type_id"
                          label={`${item.name} (${item.property_count || 0})`}
                          isChecked={
                            formik.values.property_type_id === String(item.id)
                          }
                          onChange={(e) => {
                            const selectedId = e ? String(item.id) : "";
                            formik.setFieldValue(
                              "property_type_id",
                              selectedId
                            );
                            dispatch(
                              setSearchData({
                                ...storedSearchData,
                                propertyTypeId: selectedId,
                              })
                            );
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-end">
                    <ButtonPrimary
                      onClick={() => handleApplyFilters(close)}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  }; */

  const renderTabsPriceRage = () => {
    const isFilterApplied = priceRange[0] !== 0 || priceRange[1] !== 5000;

    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border focus:outline-none transition-all ${
                isFilterApplied
                  ? "bg-[#f5694b] text-white border-[#f5694b]"
                  : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
              }`}
            >
              Budget
              <i className="las la-angle-down ml-2"></i>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-3 left-0 sm:px-0 sm:left-0 transform -translate-x-1/2 left-1/2 sm:translate-x-0 sm:left-0">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  <div className="relative flex flex-col px-5 py-6 space-y-8">
                    <div className="space-y-5">
                      <span className="font-medium text-neutral-800 dark:text-neutral-200">
                        Price
                      </span>
                      <Slider
                        range
                        min={0}
                        max={5000}
                        step={1}
                        value={priceRange}
                        onChange={handlePriceChange}
                        trackStyle={[{ backgroundColor: "#f5694b" }]}
                        handleStyle={[
                          { borderColor: "#f5694b", backgroundColor: "#fff" },
                          { borderColor: "#f5694b", backgroundColor: "#fff" },
                        ]}
                      />
                    </div>

                    <div className="flex justify-between space-x-5">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          Min price
                        </label>
                        <div className="mt-1 relative rounded-md">
                          <input
                            type="text"
                            disabled
                            id="minPrice"
                            className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-3 sm:text-sm border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                            value={`$${convertNumbThousand(priceRange[0])}`}
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="maxPrice"
                          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                        >
                          Max price
                        </label>
                        <div className="mt-1 relative rounded-md">
                          <input
                            type="text"
                            disabled
                            name="maxPrice"
                            id="maxPrice"
                            className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-3 sm:text-sm border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                            value={`$${convertNumbThousand(priceRange[1])}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                    <ButtonThird
                      onClick={() => handleClear(close)}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={() => handleApplyFilters(close)}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  

  const renderTabsDates = () => {
    const [start, end] = datesRange;
    const isFilterApplied = !!start || !!end || !!formik.values.minStayMonths;

    const addMonths = (date: Date, months: number) => {
      const result = new Date(date);
      const d = result.getDate();
      result.setMonth(result.getMonth() + months);

      if (result.getDate() < d) {
        result.setDate(0);
      }
      return result;
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
      const [newStart, newEnd] = dates;
      setDatesRange([newStart, newEnd]);
      formik.setFieldValue("startDate", newStart);
      formik.setFieldValue("endDate", newEnd);

      dispatch(
        setSearchData({
          ...storedSearchData,
          startDate: newStart?.toISOString() ?? null,
          endDate: newEnd?.toISOString() ?? null,
        })
      );
    };

    const handleMinStayChange = (months: number) => {
      const currentValue = formik.values.minStayMonths;
      const newValue = currentValue === months ? null : months;
      formik.setFieldValue("minStayMonths", newValue);

      if (newValue && start) {
        const newEnd = addMonths(start, newValue);
        setDatesRange([start, newEnd]);
        formik.setFieldValue("endDate", newEnd);

        dispatch(
          setSearchData({
            ...storedSearchData,
            startDate: start?.toISOString() ?? null,
            endDate: newEnd.toISOString(),
          })
        );
      } else if (!newValue) {
        setDatesRange([start, end]);
        formik.setFieldValue("endDate", end);
      }
    };

    const clearDates = () => {
      setDatesRange([null, null]);
      formik.setFieldValue("startDate", null);
      formik.setFieldValue("endDate", null);
      formik.setFieldValue("minStayMonths", null);

      dispatch(
        setSearchData({
          ...storedSearchData,
          startDate: null,
          endDate: null,
          minStayMonths: null,
        })
      );
    };

    const minimumStayOptions = [5, 6, 12];

    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border focus:outline-none transition-all ${
                isFilterApplied
                  ? "bg-[#f5694b] text-white border-[#f5694b]"
                  : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
              }`}
            >
              <span>
                {start
                  ? `${start.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}${
                      end
                        ? ` - ${end.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}`
                        : ""
                    }${
                      formik.values.minStayMonths
                        ? ` • ${formik.values.minStayMonths} mo min`
                        : ""
                    }`
                  : "Dates"}
              </span>

              {isFilterApplied && renderXClear()}
              <CalendarIcon className="w-4 h-4 ml-2" />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-lg px-4 mt-3 left-0 sm:px-0 lg:max-w-xl">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 p-5">
                 <DatePicker
  selected={start}
  onChange={handleDateChange}
  startDate={start}
  endDate={end}
  selectsRange
  inline
  monthsShown={2}
  calendarClassName="!border-0 !shadow-none font-sans"
  dayClassName={(date) => {
    const isStart = start && date.toDateString() === start.toDateString();
    const isEnd = end && date.toDateString() === end.toDateString();
    const inRange = start && end && date > start && date < end;
    const isToday = date.toDateString() === new Date().toDateString();

    if (isStart || isEnd)
      return "!bg-[#f5694b] !text-white !rounded-full font-bold";
    if (inRange)
      return "!bg-[#fff4f1] !text-[#c94a30] !rounded-none";
    if (isToday)
      return "!font-bold !text-[#f5694b]";
    return "!rounded-full hover:!bg-[#fff4f1] hover:!text-[#f5694b]";
  }}
  renderCustomHeader={({
    monthDate,
    customHeaderCount,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="flex items-center justify-between px-3 py-3 bg-gradient-to-r from-[#f5694b] to-[#e2583d] relative overflow-hidden">
      {/* diagonal pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, white 0px, white 1px, transparent 1px, transparent 10px)",
        }}
      />
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        type="button"
        className={`relative z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30 ${
          customHeaderCount === 1 ? "invisible" : ""
        } ${prevMonthButtonDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>

      <span className="relative z-10 text-white font-bold text-sm tracking-wide">
        {monthDate.toLocaleString("en-US", { month: "long", year: "numeric" })}
      </span>

      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        type="button"
        className={`relative z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30 ${
          customHeaderCount === 0 ? "invisible" : ""
        } ${nextMonthButtonDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  )}
/>

                  <div className="mt-4">
                    <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Expected Minimum Stay
                    </span>
                    <div className="flex gap-2">
                      {minimumStayOptions.map((months) => {
                        const isActive = formik.values.minStayMonths === months;
                        const isDisabled = !start;

                        return (
                          <button
                            key={months}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => handleMinStayChange(months)}
                            className={`relative inline-flex items-center justify-center h-8 px-4 rounded-full border text-sm font-light whitespace-nowrap transition ${
                              isDisabled
                                ? "opacity-40 cursor-not-allowed border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-600"
                                : isActive
                                ? "border-[#f5694b] bg-[#f5694b] text-white"
                                : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:border-[#f5694b] hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            }`}
                          >
                            {months} {months === 1 ? "month" : "months"}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex justify-end mt-4 space-x-2">
                    <ButtonThird onClick={clearDates} sizeClass="px-4 py-2">
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={() => handleApplyFilters(close)}
                      sizeClass="px-4 py-2"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderTabsRoomAndBeds = () => {
    const isFilterApplied =
      Number(formik.values.bedrooms) > 0 || Number(formik.values.bathrooms) > 0;

    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border focus:outline-none transition-all ${
                open ? "!border-primary-500" : ""
              } ${
                isFilterApplied
                  ? "bg-[#f5694b] text-white border-[#f5694b]"
                  : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
              }`}
            >
              <span>Rooms & Beds</span>
              <i className="las la-angle-down ml-2"></i>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-3 left-0 sm:px-0 lg:max-w-md">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  <div className="relative flex flex-col px-5 py-6 space-y-5">
                    <SearchInput
                      label="Bedrooms"
                      max={10}
                      value={Number(formik.values.bedrooms) || 0}
                      onChange={(value) => {
                        formik.setFieldValue("bedrooms", value);
                        dispatch(
                          setSearchData({
                            ...storedSearchData,
                            bedroom: value,
                          })
                        );
                      }}
                    />
                    <SearchInput
                      label="Bathrooms"
                      max={10}
                      value={Number(formik.values.bathrooms) || 0}
                      onChange={(value) => {
                        formik.setFieldValue("bathrooms", value);
                        dispatch(
                          setSearchData({
                            ...storedSearchData,
                            bathroom: value,
                          })
                        );
                      }}
                    />
                  </div>

                  <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                    <ButtonThird
                      onClick={() => handleClearRoomAndBeds(close)}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>

                    <ButtonPrimary
                      onClick={() => handleApplyFilters(close)}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderTabMobileFilter = () => {
    const appliedFiltersCount =
      (formik.values.amenities?.length ? 1 : 0) +
      (formik.values.property_category?.length ? 1 : 0) +
      (formik.values.room_type_id?.length ? 1 : 0) +
      (formik.values.features?.length ? 1 : 0);

    return (
      <div>
        <div
          className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border cursor-pointer focus:outline-none transition-all ${
            appliedFiltersCount > 0
              ? "bg-[#f5694b] text-white border-[#f5694b]"
              : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
          }`}
          onClick={openModalMoreFilter}
        >
          <span>
            <span className="hidden sm:inline">More</span> filters
            {appliedFiltersCount > 0 && ` (${appliedFiltersCount})`}
          </span>
          {appliedFiltersCount > 0 && renderXClear()}
        </div>

        <Transition appear show={isOpenMoreFilter} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 overflow-y-auto"
            onClose={closeModalMoreFilter}
          >
            <div className="min-h-screen text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-60" />
              </Transition.Child>
              <span
                className="inline-block h-screen align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>

              <Transition.Child
                className="inline-block py-8 px-2 h-screen w-full max-w-4xl"
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <div className="inline-flex flex-col w-full max-w-4xl text-left align-middle transition-all transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 shadow-xl h-full">
                  <div className="relative flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 text-center">
                    <Dialog.Title className="text-lg font-medium leading-6 text-neutral-900 dark:text-neutral-100">
                      More filters
                    </Dialog.Title>
                    <span className="absolute left-3 top-3">
                      <ButtonClose onClick={closeModalMoreFilter} />
                    </span>
                  </div>

                  <div className="flex-grow overflow-y-auto">
                    <div className="px-4 sm:px-6 divide-y divide-neutral-200 dark:divide-neutral-800">
                      <div className="py-7">
                        <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                          Amenities
                        </h3>

                        <div className="relative grid grid-cols-2 gap-4 p-4">
                          {AmenitiesCategories?.map((amenity: any) => (
                            <div
                              key={amenity.id}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                name="amenities"
                                isChecked={formik.values.amenities?.includes(
                                  String(amenity.id)
                                )}
                                onChange={(e) => {
                                  const selectedAmenities = e
                                    ? [
                                        ...formik.values.amenities,
                                        String(amenity.id),
                                      ]
                                    : formik.values.amenities.filter(
                                        (a: string) => a !== String(amenity.id)
                                      );
                                  formik.setFieldValue(
                                    "amenities",
                                    selectedAmenities
                                  );
                                  dispatch(
                                    setSearchData({
                                      ...storedSearchData,
                                      selectedAmenitiesId: selectedAmenities,
                                    })
                                  );
                                }}
                              />
                              <label
                                htmlFor={`amenity-${amenity.id}`}
                                className="flex items-center text-neutral-700 dark:text-neutral-300"
                              >
                                {getIconComponent(amenity.icon_name)?.({
                                  className: "text-2xl mr-2",
                                })}
                                {amenity.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="py-7">
                        <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                          Room types
                        </h3>
                        <div className="relative grid grid-cols-2 gap-4 p-4">
                          {roomTypes?.map((room: RoomType) => (
                            <div
                              key={room.id}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                name="room_type_id"
                                isChecked={
                                  formik.values.room_type_id === String(room.id)
                                }
                                onChange={(e) => {
                                  const selectedRoomType = e
                                    ? String(room.id)
                                    : "";
                                  formik.setFieldValue(
                                    "room_type_id",
                                    selectedRoomType
                                  );
                                  dispatch(
                                    setSearchData({
                                      ...storedSearchData,
                                      roomTypeId: selectedRoomType,
                                    })
                                  );
                                }}
                              />
                              <label
                                htmlFor={`room_type_id-${room.id}`}
                                className="text-neutral-700 dark:text-neutral-300"
                              >
                                {room.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="py-7">
                        <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                          Property Category
                        </h3>
                        <div className="mt-6 relative">
                          <div className="relative grid grid-cols-2 gap-4 p-4">
                            {propertiesCategories?.map((item: any) => (
                              <div
                                key={item.id}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  name="property_category"
                                  isChecked={formik.values.property_category?.includes(
                                    String(item.id)
                                  )}
                                  onChange={(e) => {
                                    const selectedTypes = e
                                      ? [
                                          ...(Array.isArray(
                                            formik.values.property_category
                                          )
                                            ? formik.values.property_category
                                            : []),
                                          String(item.id),
                                        ]
                                      : Array.isArray(
                                          formik.values.property_category
                                        )
                                      ? formik.values.property_category.filter(
                                          (t: string) => t !== String(item.id)
                                        )
                                      : [];
                                    formik.setFieldValue(
                                      "property_category",
                                      selectedTypes
                                    );
                                    dispatch(
                                      setSearchData({
                                        ...storedSearchData,
                                        propertyCategories: selectedTypes,
                                      })
                                    );
                                  }}
                                />
                                <label
                                  htmlFor={`type-${item.id}`}
                                  className="text-neutral-700 dark:text-neutral-300"
                                >
                                  {item.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="py-7">
                        <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                          Property feature
                        </h3>
                        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                          {featuresCategories?.map((item: any) => {
                            const idStr = String(item.id);
                            const isChecked =
                              formik.values.features?.includes(idStr);

                            return (
                              <div
                                key={item.id}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  name="features"
                                  isChecked={isChecked}
                                  onChange={() => {
                                    let updatedFeatures;
                                    if (isChecked) {
                                      updatedFeatures =
                                        formik.values.features.filter(
                                          (f: string) => f !== idStr
                                        );
                                    } else {
                                      updatedFeatures = [
                                        ...formik.values.features,
                                        idStr,
                                      ];
                                    }
                                    formik.setFieldValue(
                                      "features",
                                      updatedFeatures
                                    );
                                    dispatch(
                                      setSearchData({
                                        ...storedSearchData,
                                        feature: updatedFeatures,
                                      })
                                    );
                                  }}
                                />
                                <label
                                  htmlFor={`type-${item.id}`}
                                  className="flex items-center text-neutral-700 dark:text-neutral-300"
                                >
                                  {getIconComponent(item.icon_name)?.({
                                    className: "text-2xl mr-2",
                                  })}
                                  {item.name}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6 flex-shrink-0 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                    <ButtonThird
                      onClick={handleClearParticularFilters}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>

                    <ButtonPrimary
                      onClick={() => handleApplyFilters(closeModalMoreFilter)}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      </div>
    );
  };

  return (
    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 lg:gap-4 w-full">
      <PropertyTypeFilter
        filteredPropertyTypes={filteredPropertyTypes}
        selectedPropertyTypeIds={formik.values.property_type_ids}
        onPropertyTypeChange={handlePropertyTypeChange}
        onApply={handleApplyFilters}
      />
      <PriceRangeFilter
        priceRange={priceRange}
        onPriceChange={handlePriceChange}
        onClear={handleClear}
        onApply={handleApplyFilters}
      />
      <RoomsAndBedsFilter
        bedrooms={formik.values.bedrooms}
        bathrooms={formik.values.bathrooms}
        onBedroomsChange={handleBedroomsChange}
        onBathroomsChange={handleBathroomsChange}
        onClear={handleClearRoomAndBeds}
        onApply={handleApplyFilters}
      />
      <DatesFilter
        datesRange={datesRange}
        minStayMonths={formik.values.minStayMonths}
        onDateChange={handleDateChange}
        onMinStayChange={handleMinStayChange}
        onClear={clearDates}
        onApply={handleApplyFilters}
        renderXClear={renderXClear}
      />
      <MobileFiltersModal
        isOpen={isOpenMoreFilter}
        onClose={closeModalMoreFilter} // ✅ Closes modal
        onOpen={openModalMoreFilter} // ✅ Opens modal
        amenities={formik.values.amenities}
        features={formik.values.features}
        propertyCategory={formik.values.property_category}
        roomTypeId={formik.values.room_type_id}
        AmenitiesCategories={AmenitiesCategories}
        featuresCategories={featuresCategories}
        propertiesCategories={propertiesCategories}
        roomTypes={roomTypes}
        onAmenitiesChange={handleAmenitiesChange}
        onFeaturesChange={handleFeaturesChange}
        onPropertyCategoryChange={handlePropertyCategoryChange}
        onRoomTypeChange={handleRoomTypeChange}
        onClear={handleClearParticularFilters}
        onApply={handleApplyFilters}
        renderXClear={renderXClear}
      />
    </div>
  );
};

export default TabFilters;
